import { useMutation } from '@tanstack/react-query';
import { resendMFA } from '@/features/auth/services/auth';
import type {
  MFAResendRequest,
  MFAResendResponse,
} from '@/features/auth/types/auth.types';

interface UseResendMFAOptions {
  onSuccess?: (data: MFAResendResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * useResendMFA
 *
 * Hook for resending MFA token to the user.
 * Should be used together with useResendTimer to control resend frequency.
 *
 * ## Features
 * - **Token Resend**: Requests a new MFA code using session ID
 * - **Error Handling**: Provides callbacks for success and error states
 * - **Loading State**: Exposes isPending state for UI feedback
 *
 * ## Flow Diagram
 *
 * ```mermaid
 * flowchart TD
 *     A[User clicks resend] --> B{Can resend?}
 *     B -->|No| C[Button disabled]
 *     B -->|Yes| D[Call mutate]
 *     D --> E[API Request]
 *     E --> F{Response}
 *     F -->|Success| G[onSuccess callback]
 *     F -->|Error| H[onError callback]
 *     G --> I[Start timer]
 *     H --> J[Show error]
 * ```
 *
 * @param options - Callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 *
 * @example
 * Basic usage
 * ```tsx
 * const { mutate: resend, isPending } = useResendMFA({
 *   onSuccess: () => {
 *     message.success('Code resent successfully');
 *     startTimer();
 *   },
 *   onError: (error) => {
 *     message.error('Failed to resend code');
 *   }
 * });
 *
 * // Trigger resend
 * resend({ sessionId: 'session-123' });
 * ```
 *
 * @example
 * With timer integration
 * ```tsx
 * const { mutate: resend, isPending } = useResendMFA({
 *   onSuccess: () => {
 *     startTimer(); // Restart cooldown timer
 *   }
 * });
 *
 * const { canResend, timeRemaining, startTimer } = useResendTimer();
 *
 * <Button
 *   onClick={() => resend({ sessionId })}
 *   disabled={!canResend || isPending}
 * >
 *   {canResend ? 'Resend code' : `Wait ${timeRemaining}s`}
 * </Button>
 * ```
 */
export function useResendMFA(options?: UseResendMFAOptions) {
  return useMutation<MFAResendResponse, Error, MFAResendRequest>({
    mutationFn: (payload) => resendMFA(payload),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
